import { apiGetCustomers } from "@lib/api/customer";
import { CustomerName } from "@lib/components/MiniComponents/Others";
import { createYupSync } from "@lib/helpers/TableHelper";
import { walletAddressSchema as yupSchema } from "@lib/schemas/wallet";
import { PaginationType, UseQueryProps } from "@lib/types/Api";
import { Wallet, WalletAddress, WalletAddressStatus } from "@lib/types/Billing";
import { SendByteWallet, SendByteWalletAddress } from "@lib/types/SendByte";
import { AIWallet, AIWalletAddress } from "@lib/types/Wallet";
import { useQuery } from "@tanstack/react-query";
import { Form, Input, Select } from "antd";
import { FormInstance } from "antd/lib";
import { useEffect, useMemo } from "react";
import { useIntl } from "react-intl";

type Props = {
  walletsQuery: UseQueryProps<
    PaginationType<Wallet | SendByteWallet | AIWallet>
  >;
  walletAddress?: Partial<
    WalletAddress | AIWalletAddress | SendByteWalletAddress
  >;
  form: FormInstance<any>;
  loading?: boolean;
  value?: string;
};

export default function WalletCustomerIdInput(props: Props) {
  const { walletsQuery, loading, form, value, walletAddress } = props;
  const { messages } = useIntl();
  const walletId = Form.useWatch("walletId", form);
  const customerId = Form.useWatch("customerId", form);
  const status = Form.useWatch("status", form);

  const walletCustomerId = useMemo(() => {
    return (
      walletsQuery.data?.data?.data?.find(
        (wallet) => wallet.id === walletId,
      ) as any
    )?.customerId;
  }, [walletsQuery.data?.data?.data, walletId]);

  const {
    data: customersData,
    isLoading: customersIsLoading,
    isFetching: customersIsFetching,
  } = useQuery({
    queryKey: ["customers-dropdown"],
    queryFn: () =>
      apiGetCustomers({
        sort: "name,ASC",
        limit: 100,
      }),
  });
  const apiData = customersData?.data?.data;

  useEffect(() => {
    if (!form) return;
    if (value) {
      form.setFieldValue("customerId", value);
      return;
    }
    if (walletCustomerId) {
      form.setFieldValue("customerId", walletCustomerId);
    }
  }, [walletId, value, walletCustomerId, form, apiData]);

  if (!!walletAddress?.customerId && status === WalletAddressStatus.InUse) {
    return (
      <Form.Item
        name="customerId"
        rules={[createYupSync({ schema: yupSchema })]}
        label={messages["common.customer"] as string}
      >
        <Input
          variant="borderless"
          disabled
          prefix={
            <CustomerName
              customer={apiData?.find(
                (customer) => customer.id === walletAddress?.customerId,
              )}
            />
          }
        />
      </Form.Item>
    );
  }

  return (
    <Form.Item
      shouldUpdate
      name="customerId"
      rules={[createYupSync({ schema: yupSchema })]}
      label={messages["common.customer"] as string}
      hasFeedback
      validateStatus={customerId ? "success" : "warning"}
      extra={
        walletId ? null : (messages["common.pleaseFirstSelectWallet"] as string)
      }
    >
      <Select
        allowClear
        placeholder={messages["common.makeYourChoice"] as string}
        onChange={(value) => {
          form.setFieldValue("customerId", value);
        }}
        loading={customersIsLoading || customersIsFetching}
        disabled={loading || customersIsLoading || customersIsFetching}
        showSearch
        optionFilterProp="text"
        options={apiData?.map((customer) => ({
          label: <CustomerName customer={customer} />,
          text: customer.name,
          value: customer.id,
        }))}
      />
    </Form.Item>
  );
}
